<template>
    <div :class="getLayoutClass">
        <template v-if="showSideColumn && !isAdsenseGamingPage">
            <SideColumn direction="left" />
            <DynamicTemplate></DynamicTemplate>
            <SideColumn direction="right" />
        </template>
        <template v-else>
            <DynamicTemplate></DynamicTemplate>
        </template>

        <CookieBannerController
            v-if="showCookieBanner"
        ></CookieBannerController>

        <ReliableSiteBadge
            v-if="$globalConstants.host['reliable_site_badge']"
            :script-string="$globalConstants.host?.reliable_site_badge_script"
        />

        <VerifiedBadge
            v-if="$globalConstants.host?.trust_badge"
            :showOnScroll="
                !!$globalConstants.host?.trust_badge_upon_scrolling || false
            "
        />

        <QuestionnaireApp
            v-if="quizState.showQuestionnaire && !isAdsenseGamingPage"
            v-bind="$globalConstants.components.Quiz"
        />

        <Plus18Banner v-if="$globalConstants.host.plus18_popup" />
    </div>
</template>

<script>
import { quizStore } from '@/store';
import DynamicTemplate from '@/components/DynamicTemplate';
import { mediaQueriesMixin, routeMixin } from './mixins';
import SideColumn from '@layouts/SideColumn/SideColumn.vue';
import { User } from './models/user';
import {
    getStorageItemWithExpiry,
    setStorageItemWithExpiry,
} from '@story/utils/localstorage';

export default {
    name: 'DynamicTemplateApp',
    mixins: [quizStore, routeMixin, mediaQueriesMixin],
    components: {
        DynamicTemplate,
        SideColumn,
        VerifiedBadge: () =>
            import(
                /* webpackChunkName: "VerifiedBadge" */ '@molecules/VerifiedBadge/VerifiedBadge.vue'
            ),
        QuestionnaireApp: () =>
            import(
                /* webpackChunkName: "QuestionnaireApp" */ '@/design-system/questionnaire/QuestionnaireApp'
            ),
        Plus18Banner: () =>
            import(
                /* webpackChunkName: "Plus18Banner" */ '@molecules/Plus18Banner/Plus18Banner'
            ),
        CookieBannerController: () =>
            import(
                /* webpackChunkName: "CookieBannerController" */ '@organisms/CookieBanner/CookieBannerController'
            ),
        ReliableSiteBadge: () =>
            import(
                /* webpackChunkName: "ReliableSiteBadge" */ '@atoms/ReliableSiteBadge/ReliableSiteBadge'
            ),
    },
    beforeMount() {
        this.$globalState.fixedPosition = !!this.$route.query.fp;
    },
    mounted() {
        this.quizState = quizStore.state;
        this.menuItems = window.__menu_items__;
        setTimeout(() => this.setQuestionnaire(this.getCategorySlug()), 500);

        this.fireAbTest();
    },
    data() {
        return {
            quizState: {},
            loaded: false,
            menuItems: [],
            showCookieBanner: this.showCookieBannerOverlay(),
        };
    },
    methods: {
        setQuestionnaire(category) {
            this.setCategoryQuestionnaire(category, this.$globalConstants.host);
        },
        showCookieBannerOverlay() {
            return Boolean(
                [2, 3, 4].includes(
                    parseInt(this.$globalConstants.host['cookie_banner'])
                )
            );
        },
        async fireAbTest() {
            const isBot = window['__traffic_source__'] === 'bot';
            const AB_TEST_TTL = 60 * 60 * 1000; // one hour
            const AB_TEST_KEY = 'SSR_AB_TEST_FIRED';

            if (
                isBot ||
                !this.isABTestDomain ||
                getStorageItemWithExpiry(AB_TEST_KEY) !== null
            ) {
                return;
            }

            const DEFAULT_TEST_NAME = 'SSR_TEMPLATE_TEST';
            const TEST_NAME =
                window?.__host__?.ssr_ab_test_name || DEFAULT_TEST_NAME;

            const params = new URLSearchParams({
                test_name: TEST_NAME,
                test_variant: 'Default',
                token: this.token,
                clickin_id: this.clickInId,
            }).toString();

            const url = `/api/host-ab-test-postback?${params}`;

            try {
                await fetch(url, {
                    method: 'GET',
                    cache: 'no-store',
                });
                setStorageItemWithExpiry(AB_TEST_KEY, true, AB_TEST_TTL);
                this.$globalState.abTestFired = true;
            } catch (err) {
                console.error('AB callback error:', err);
            }
        },
    },
    computed: {
        getLayoutClass() {
            if (this.isAdsenseGamingPage) return '';
            return this.showSideColumn ? 'sidebars-layout' : '';
        },
        isAdsenseGamingPage() {
            return this.$route.name === 'adsense-game-page';
        },
        showSideColumn() {
            return (
                !this.isMobileDevice() &&
                this.$globalConstants.components?.Settings?.pageLayout ===
                    'sidebars layout'
            );
        },
        isOnPageQuiz() {
            // activate portal if host/category questionnaire type is on_page
            const categoryQuiz =
                this.$globalConstants.host.categories_quiz_mapping?.[
                    this.getCategorySlug()
                ];
            return categoryQuiz?.quiz_template
                ? categoryQuiz.quiz_template === 'Que_OnPage'
                : this.$globalConstants.host.quiz_template === 'Que_OnPage';
        },
        hostName() {
            return this.$globalConstants.host['name'];
        },
        token() {
            return User.getInstance().token;
        },
        clickInId() {
            return User.getInstance().clickinId;
        },
        isABTestDomain() {
            if (process.env.VUE_APP_AB_TEST_DOMAINS) {
                const domains = process.env.VUE_APP_AB_TEST_DOMAINS.split(',');
                return domains.includes(this.hostName);
            }
            return false;
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/_themes.scss';
@import '@/sass/mixins';
@import '@/sass/font-mixin';
@import '@/sass/font-variables';
@import '@/sass/color-variables';
@import '@/sass/_base.scss';
@import '@/sass/_reset.scss';
@import '@/sass/components/card-container';
@import '@/sass/components/content-column';

/*! purgecss start ignore */
.vue-suspense-wrapper {
    width: 100%;
}
/*! purgecss end ignore */
</style>

<style lang="scss" scoped>
.questionnaire-app {
    width: 100%;
    position: absolute;
    z-index: 100;
}

.sidebars-layout {
    display: grid;
    grid-template-columns: auto 1000px auto;
    .page-container {
        grid-column: 2;
    }
}
</style>
